// ============================================================================
// JOB COSTING DASHBOARD
// Track material costs, labor hours, outsourcing fees, and profit margins
// Reusable across industry solutions (manufacturing, service-operations, etc.)
// Date: January 2026, Updated March 2026
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/job-costing.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/job-costing)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DollarSign, TrendingUp, RefreshCw,
  Search, ChevronRight, ChevronDown, Package, Clock,
  Users, Truck, Target,
  PieChart, BarChart3, ArrowUp, ArrowDown, Minus,
  Download, Calculator, Percent, Layers, Receipt
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { readJsonResponse } from '@/lib/readJsonResponse';
import { emitOperationsGuidedTourTrainingEvent } from '@/lib/operations/guidedTourEvents';
import { useOpenTeamMember } from '@/hooks/useOpenTeamMember';
import {
  PieChart as RechartsPie, Pie, Cell,
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ComposedChart
} from 'recharts';
import { isTerminalWorkItemStatus } from '@/lib/work-items/operationalStatusConfig';
import { createLogger } from '@/lib/debugLogger';
import type { StatCard } from '@/components/modules/ModuleHeader';
import UserDocsLink from '@/components/shared/UserDocsLink';

const logger = createLogger('JobCostingDashboard');

// ============================================================================
// TYPES
// ============================================================================

export interface JobCost {
  id: string;
  job_number: string;
  job_title: string;
  share_token?: string | null;
  customer_name?: string;
  status: string;
  manager_id?: number | null;
  manager_name?: string | null;
  material_cost: number;
  labor_cost: number;
  bom_labor_cost: number;
  timer_labor_cost: number;
  /** TE-* BOM lines at sell rate — billed revenue, excluded from labor_cost */
  billed_time_amount?: number;
  billed_labour_hours?: number;
  labor_hours: number;
  bom_labor_hours: number;
  timer_labor_hours: number;
  outsourcing_cost: number;
  overhead_cost: number;
  expense_cost: number;
  total_cost: number;
  quoted_price: number;
  actual_revenue: number;
  billing_submission_count: number;
  /** Work submitted for billing but not yet approved (draft/pending submissions) */
  pending_billing_amount?: number;
  effective_revenue: number;
  profit_margin: number;
  profit_percentage: number;
  is_profitable: boolean;
  has_bom_data: boolean;
  bom_line_count: number;
  has_timer_data: boolean;
  has_billing_data: boolean;
  item_type?: string;
  completed_at?: string;
  created_at: string;
}

interface CostSummary {
  totalMaterial: number;
  totalLabor: number;
  totalBomLabor: number;
  totalTimerLabor: number;
  totalBilledTime?: number;
  totalBilledLabourHours?: number;
  totalOutsourcing: number;
  totalOverhead: number;
  totalExpenses: number;
  totalCost: number;
  totalQuoted: number;
  totalActualRevenue: number;
  totalPendingBilling?: number;
  totalEffectiveRevenue: number;
  totalProfit: number;
  avgProfitMargin: number;
  profitableJobs: number;
  unprofitableJobs: number;
  totalJobs: number;
  jobsWithBom: number;
  jobsWithTimers: number;
  jobsWithBilling: number;
  totalLaborHours: number;
  totalBomLaborHours: number;
  totalTimerLaborHours: number;
}

interface TeamMemberCost {
  user_id: number;
  user_name: string;
  root_item_ids: string[];
  item_breakdown: Array<{
    work_item_id: string;
    total_hours: number;
    billable_hours: number;
    non_billable_hours: number;
    travel_hours: number;
    onsite_hours: number;
    billable_travel_hours: number;
    billable_onsite_hours: number;
    travel_distance_km: number;
    billable_travel_distance_km: number;
    /** null when the viewer is not owner/root — labour cost is restricted */
    time_cost: number | null;
    estimated_recovery: number;
    recovery_margin: number | null;
  }>;
  metrics: {
    total_hours: number;
    billable_hours: number;
    non_billable_hours: number;
    travel_hours: number;
    onsite_hours: number;
    billable_travel_hours: number;
    billable_onsite_hours: number;
    travel_distance_km: number;
    billable_travel_distance_km: number;
    /** null when labour cost is restricted (non-owner/root) */
    time_cost: number | null;
    estimated_recovery: number;
    items_worked: number;
    tasks_assigned: number;
    tasks_completed: number;
    appointments_assigned: number;
    appointments_completed: number;
    appointments_cancelled: number;
    appointments_missed: number;
  };
  /** null when labour cost is restricted (non-owner/root) */
  rates: {
    cost_rate: number;
    cost_rate_source?: 'client_monthly_cost' | 'employee_hourly_rate' | 'contract_hourly' | 'contract_salary' | 'none' | null;
    target_recovery_rate: number | null;
    monthly_target: number | null;
    annual_target: number | null;
    billable_hours_target: number | null;
  } | null;
  /** null when labour cost is restricted (non-owner/root) */
  profitability: {
    estimated_recovery: number;
    target_recovery_amount: number;
    recovery_margin: number;
    recovery_margin_pct: number;
    target_attainment_pct: number | null;
  } | null;
  scores: {
    billable_ratio: number;
    completion_rate: number;
    missed_rate: number;
    travel_efficiency: number;
  };
}

interface TeamSummary {
  total_members: number;
  total_hours: number;
  total_billable_hours: number;
  total_travel_hours: number;
  total_time_cost: number;
  total_appointments_missed: number;
  avg_billable_ratio: number;
}

interface JobCostingApiMeta {
  team_data_source?: 'job_costing_api';
  root_item_count?: number;
  descendant_item_count?: number;
  scoped_item_count?: number;
  include_descendants?: boolean;
}

type JobCostingPermissions = {
  can_view?: boolean;
  can_create?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  can_admin?: boolean;
};

type TeamSortOption = 'cost_desc' | 'hours_desc' | 'billable_desc' | 'name_asc' | 'efficiency_desc';

// ============================================================================
// CONSTANTS
// ============================================================================

const COST_CATEGORIES = [
  { key: 'material', label: 'Material/BOM', color: '#3b82f6', icon: Package },
  { key: 'labor', label: 'Labor (BOM + Timer)', color: '#10b981', icon: Users },
  { key: 'outsourcing', label: 'Outsourcing', color: '#f59e0b', icon: Truck },
  { key: 'overhead', label: 'Overhead', color: '#8b5cf6', icon: Layers },
  { key: 'expense', label: 'Expenses', color: '#ec4899', icon: Receipt }
];

const getFilteredCostCategories = (showOutsourcing: boolean, hasExpenses: boolean = false) => {
  let cats = showOutsourcing
    ? COST_CATEGORIES
    : COST_CATEGORIES.filter(cat => cat.key !== 'outsourcing');
  if (!hasExpenses) {
    cats = cats.filter(cat => cat.key !== 'expense');
  }
  return cats;
};

const EMPTY_COST_SUMMARY: CostSummary = {
  totalMaterial: 0,
  totalLabor: 0,
  totalBomLabor: 0,
  totalTimerLabor: 0,
  totalBilledTime: 0,
  totalBilledLabourHours: 0,
  totalOutsourcing: 0,
  totalOverhead: 0,
  totalExpenses: 0,
  totalCost: 0,
  totalQuoted: 0,
  totalActualRevenue: 0,
  totalPendingBilling: 0,
  totalEffectiveRevenue: 0,
  totalProfit: 0,
  avgProfitMargin: 0,
  profitableJobs: 0,
  unprofitableJobs: 0,
  totalJobs: 0,
  jobsWithBom: 0,
  jobsWithTimers: 0,
  jobsWithBilling: 0,
  totalLaborHours: 0,
  totalBomLaborHours: 0,
  totalTimerLaborHours: 0,
};

const EMPTY_TEAM_SUMMARY: TeamSummary = {
  total_members: 0,
  total_appointments_missed: 0,
  total_hours: 0,
  total_billable_hours: 0,
  total_travel_hours: 0,
  total_time_cost: 0,
  avg_billable_ratio: 0,
};

const formatItemTypeLabel = (itemType: string) => itemType
  .split(/[_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
  .join(' ');

const pluralizeLabel = (label: string) => {
  if (label.endsWith('s')) return label;
  if (label.endsWith('y')) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
};

const joinLabels = (labels: string[]) => {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
};

const normalizeRootItemIds = (rawRootItemIds: unknown): string[] => {
  if (typeof rawRootItemIds === 'string') {
    return Array.from(new Set(rawRootItemIds.split(',').map((itemId) => itemId.trim()).filter(Boolean)));
  }

  if (Array.isArray(rawRootItemIds)) {
    return Array.from(new Set(
      rawRootItemIds
        .flatMap((itemId) => typeof itemId === 'string' ? itemId.split(',') : [])
        .map((itemId) => itemId.trim())
        .filter(Boolean)
    ));
  }

  return [];
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function CostCard({ 
  title, 
  value, 
  trend, 
  icon: Icon,
  color,
  subtitle
}: {
  title: string;
  value: number;
  trend?: number;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  return (
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
          <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            R{(value ?? 0).toLocaleString()}
          </p>
          {subtitle && (
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>
          )}
          {trend !== undefined && (
            <div className="flex items-center gap-1 mt-2">
              {trend > 0 ? (
                <ArrowUp className="w-3 h-3 text-green-500" />
              ) : trend < 0 ? (
                <ArrowDown className="w-3 h-3 text-red-500" />
              ) : (
                <Minus className="w-3 h-3 text-gray-400" />
              )}
              <span className={`text-xs ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                {Math.abs(trend).toFixed(1)}% vs last month
              </span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function TeamMetricCard({
  icon: Icon,
  label,
  value,
  subValue,
  color,
  isDark,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  color: string;
  isDark: boolean;
}) {
  return (
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20`, color }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
          {subValue && (
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function TeamMemberExpandedContent({
  member,
  isDark,
  maxTeamHours,
  jobsById,
  handleOpenItem,
  openingItemId,
  collectionEntityLabel,
}: {
  member: TeamMemberCost;
  isDark: boolean;
  maxTeamHours: number;
  jobsById: Map<string, JobCost>;
  handleOpenItem: (item: JobCost) => Promise<void>;
  openingItemId: string | null;
  collectionEntityLabel: string;
}) {
  return (
    <div className={`px-4 py-4 ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
      <div className="grid grid-cols-1 gap-4 text-xs sm:grid-cols-2 xl:grid-cols-4">
        <div>
          <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Hours Breakdown</p>
          <div className="mt-1 space-y-1">
            <div className="flex justify-between"><span>Total</span><span className="font-medium">{formatHours(member.metrics.total_hours)}</span></div>
            <div className="flex justify-between"><span>Billable</span><span className="font-medium">{formatHours(member.metrics.billable_hours)}</span></div>
            <div className="flex justify-between"><span>Non-billable</span><span className="font-medium">{formatHours(member.metrics.non_billable_hours)}</span></div>
            <div className="flex justify-between"><span>On-site</span><span className="font-medium">{formatHours(member.metrics.onsite_hours)}</span></div>
            <div className="flex justify-between"><span>Billable on-site</span><span className="font-medium">{formatHours(member.metrics.billable_onsite_hours)}</span></div>
          </div>
        </div>
        <div>
          <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Assignments</p>
          <div className="mt-1 space-y-1">
            <div className="flex justify-between"><span>Tasks</span><span className="font-medium">{member.metrics.tasks_completed}/{member.metrics.tasks_assigned}</span></div>
            <div className="flex justify-between"><span>Appointments</span><span className="font-medium">{member.metrics.appointments_completed}/{member.metrics.appointments_assigned}</span></div>
            <div className="flex justify-between"><span>Cancelled appts</span><span className={`font-medium ${member.metrics.appointments_cancelled > 0 ? 'text-red-500' : ''}`}>{member.metrics.appointments_cancelled}</span></div>
            <div className="flex justify-between"><span>Missed appts</span><span className={`font-medium ${member.metrics.appointments_missed > 0 ? 'text-red-500' : ''}`}>{member.metrics.appointments_missed}</span></div>
          </div>
        </div>
        <div>
          <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Travel & Efficiency</p>
          <div className="mt-1 space-y-1">
            <div className="flex justify-between"><span>Travel time</span><span className="font-medium">{formatHours(member.metrics.travel_hours)}</span></div>
            <div className="flex justify-between"><span>Billable travel</span><span className="font-medium">{formatHours(member.metrics.billable_travel_hours)}</span></div>
            <div className="flex justify-between"><span>Distance</span><span className="font-medium">{member.metrics.travel_distance_km.toLocaleString('en-ZA', { maximumFractionDigits: 1 })} km</span></div>
            <div className="flex justify-between"><span>Billable km</span><span className="font-medium">{member.metrics.billable_travel_distance_km.toLocaleString('en-ZA', { maximumFractionDigits: 1 })} km</span></div>
            <div className="flex justify-between"><span>Travel efficiency</span><span className="font-medium">{Math.round(member.scores.travel_efficiency)}%</span></div>
          </div>
        </div>
        <div>
          <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Profitability</p>
          <div className="mt-1 space-y-1">
            {member.rates && member.profitability ? (
              <>
                <div className="flex justify-between"><span>Cost rate</span><span className="font-medium">{formatRate(member.rates.cost_rate)}</span></div>
                <div className="flex justify-between"><span>Target rate</span><span className="font-medium">{formatRate(member.rates.target_recovery_rate)}</span></div>
                <div className="flex justify-between"><span>Time cost</span><span className="font-medium">{formatCurrency(member.metrics.time_cost ?? 0)}</span></div>
                <div className="flex justify-between"><span>Recovery est.</span><span className="font-medium">{formatCurrency(member.profitability.estimated_recovery)}</span></div>
                <div className="flex justify-between"><span>Margin</span><span className={`font-medium ${member.profitability.recovery_margin >= 0 ? 'text-green-500' : 'text-red-500'}`}>{formatCurrency(member.profitability.recovery_margin)}</span></div>
                <div className="flex justify-between"><span>Margin %</span><span className="font-medium">{member.profitability.estimated_recovery > 0 ? `${member.profitability.recovery_margin_pct.toFixed(1)}%` : '—'}</span></div>
                <div className="flex justify-between"><span>Target attainment</span><span className="font-medium">{member.profitability.target_attainment_pct !== null ? `${member.profitability.target_attainment_pct.toFixed(0)}%` : '—'}</span></div>
              </>
            ) : (
              <p className={isDark ? 'text-gray-600' : 'text-gray-400'}>Cost &amp; margin restricted to owner</p>
            )}
            <div className="flex justify-between"><span>Billable ratio</span><span className="font-medium">{Math.round(member.scores.billable_ratio)}%</span></div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Hours utilisation</span>
          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
            {formatHours(member.metrics.total_hours)} / {formatHours(maxTeamHours)}
          </span>
        </div>
        <ProgressBar value={member.metrics.total_hours} max={maxTeamHours} color="bg-blue-500" isDark={isDark} />
      </div>

      {member.item_breakdown.length > 0 && (
        <div className="mt-4" data-tour-id="job-costing-results-table">
          <p className={`mb-2 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Related {collectionEntityLabel}
          </p>
          <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
            {member.item_breakdown.map((breakdown) => {
              const item = jobsById.get(breakdown.work_item_id);
              if (!item) return null;

              return (
                <div
                  key={breakdown.work_item_id}
                  className={`cursor-pointer rounded-lg border p-3 transition-colors ${isDark ? 'bg-gray-800 border-gray-700 hover:bg-gray-800/80' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => void handleOpenItem(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void handleOpenItem(item);
                    }
                  }}
                  aria-busy={openingItemId === item.id}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{item.job_number}</p>
                      <p className={`truncate text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.job_title}</p>
                      <p className={`mt-1 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {item.manager_name ? `Manager: ${item.manager_name} • ` : ''}
                        {openingItemId === item.id ? 'Opening item…' : 'Open details'}
                      </p>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                      {breakdown.time_cost != null ? formatCurrency(breakdown.time_cost) : formatHours(breakdown.total_hours)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                    <div>
                      <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Hours</p>
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{formatHours(breakdown.total_hours)}</p>
                    </div>
                    <div>
                      <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Billable</p>
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{formatHours(breakdown.billable_hours)}</p>
                    </div>
                    <div>
                      <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Travel</p>
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{formatHours(breakdown.travel_hours)}</p>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Recovery est.</p>
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{formatCurrency(breakdown.estimated_recovery)}</p>
                    </div>
                    <div>
                      <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Margin</p>
                      <p className={`font-medium ${(breakdown.recovery_margin ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{breakdown.recovery_margin != null ? formatCurrency(breakdown.recovery_margin) : '—'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score, isDark }: { score: number; isDark: boolean }) {
  const bg = score >= 80
    ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
    : score >= 60
      ? (isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
      : score >= 40
        ? (isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700')
        : (isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700');

  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${bg}`}>{Math.round(score)}%</span>;
}

function ProgressBar({ value, max, color, isDark }: { value: number; max: number; color: string; isDark: boolean }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className={`h-2 w-full rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '0h';
  const wholeHours = Math.floor(hours);
  const mins = Math.round((hours - wholeHours) * 60);
  if (wholeHours === 0 && mins > 0) return `${mins}m`;
  return mins > 0 ? `${wholeHours}h ${mins}m` : `${wholeHours}h`;
}

function formatCurrency(value: number): string {
  return `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatRate(value: number | null | undefined): string {
  if (!Number.isFinite(Number(value))) return '—';
  return `${formatCurrency(Number(value))}/hr`;
}

function normalizeTooltipCurrencyValue(value: number | string | ReadonlyArray<number | string> | undefined): number {
  const rawValue = Array.isArray(value) ? value[0] : value;
  const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function getCurrentReturnToPath(): string {
  if (typeof window === 'undefined') {
    return '/client-zone/dashboard';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface JobCostingDashboardProps {
  permissions?: JobCostingPermissions;
  templateKey?: string;
  itemTypes?: string | string[];
  rootItemIds?: string[] | string;
  includeDescendants?: boolean;
  title?: string;
  subtitle?: string;
  embedded?: boolean;
  showHeaderControls?: boolean;
  onOpenItem?: (item: JobCost) => void | Promise<void>;
  /** Whether to show outsourcing costs (default: true for manufacturing, false for service-operations) */
  showOutsourcing?: boolean;
  /** Billing integration mode: 'direct' links material/BOM/labour directly to billing, 'billing-tab' routes through BillingDashboard tab */
  billingIntegrationMode?: 'direct' | 'billing-tab';
  /** When provided, the summary stat cards are reported to the host (shared
   *  StrictModuleHeader Row C) and not rendered inline. */
  onStatsChange?: (cards: StatCard[]) => void;
}

export default function JobCostingDashboard({
  permissions: _permissions,
  templateKey,
  itemTypes,
  rootItemIds,
  includeDescendants = true,
  title,
  subtitle,
  embedded = false,
  showHeaderControls = true,
  onOpenItem,
  showOutsourcing = true,
  billingIntegrationMode = 'billing-tab',
  onStatsChange,
}: JobCostingDashboardProps) {
  void _permissions;

  const router = useRouter();
  const openTeamMember = useOpenTeamMember();
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || '#6366f1';

  const normalizedItemTypes = useMemo(() => {
    if (typeof itemTypes === 'string') {
      const normalized = itemTypes
        .split(',')
        .map((itemType) => itemType.trim())
        .filter(Boolean);

      return normalized.length > 0 ? normalized.join(',') : 'job,project';
    }

    if (Array.isArray(itemTypes)) {
      const normalized = itemTypes
        .flatMap((itemType) => typeof itemType === 'string' ? itemType.split(',') : [])
        .map((itemType) => itemType.trim())
        .filter(Boolean);

      return normalized.length > 0 ? normalized.join(',') : 'job,project';
    }

    return 'job,project';
  }, [itemTypes]);

  const normalizedRootItemIds = useMemo(
    () => normalizeRootItemIds(rootItemIds),
    [rootItemIds],
  );

  const requestedItemTypes = useMemo(() => normalizedItemTypes
    .split(',')
    .map((itemType) => itemType.trim())
    .filter(Boolean), [normalizedItemTypes]);

  const singularEntityLabels = useMemo(
    () => requestedItemTypes.map(formatItemTypeLabel),
    [requestedItemTypes]
  );

  const pluralEntityLabels = useMemo(
    () => singularEntityLabels.map(pluralizeLabel),
    [singularEntityLabels]
  );

  const titleEntityLabel = useMemo(
    () => joinLabels(singularEntityLabels) || 'Work Item',
    [singularEntityLabels]
  );

  const collectionEntityLabel = useMemo(
    () => joinLabels(pluralEntityLabels) || 'Work Items',
    [pluralEntityLabels]
  );

  const genericEntityLabel = useMemo(
    () => singularEntityLabels.length === 1 ? singularEntityLabels[0] : 'Work Item',
    [singularEntityLabels]
  );

  const collectionEntityLabelLower = collectionEntityLabel.toLowerCase();
  const resolvedTitle = title || `${titleEntityLabel} Costing`;
  const resolvedSubtitle = subtitle || `Track costs, margins, and profitability across ${collectionEntityLabelLower}`;

  const [jobs, setJobs] = useState<JobCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [managerFilter, setManagerFilter] = useState<number | 'all'>('all');
  // Managers seen across loaded jobs — used for the manager filter options
  const [managerOptions, setManagerOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [serverSummary, setServerSummary] = useState<CostSummary | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberCost[]>([]);
  const [serverTeamSummary, setServerTeamSummary] = useState<TeamSummary>(EMPTY_TEAM_SUMMARY);
  const [serverMeta, setServerMeta] = useState<JobCostingApiMeta | null>(null);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSortBy, setTeamSortBy] = useState<TeamSortOption>('cost_desc');
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  const [teamCollapsed, setTeamCollapsed] = useState(false);
  const [openingItemId, setOpeningItemId] = useState<string | null>(null);
  const [recognizingCOGS, setRecognizingCOGS] = useState<string | null>(null);

  const summaryCountLabel = jobs.length === 1
    ? `1 ${genericEntityLabel.toLowerCase()}`
    : `${jobs.length} ${collectionEntityLabelLower}`;

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      // Use dedicated job-costing API that aggregates from work_item_bom_items
      const response = await fetch('/api/v1/job-costing', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_key: templateKey || undefined,
          item_types: normalizedItemTypes,
          root_item_ids: normalizedRootItemIds.length > 0 ? normalizedRootItemIds : undefined,
          include_descendants: includeDescendants,
          limit: 500,
          manager_id: managerFilter === 'all' ? undefined : managerFilter,
        })
      });

      const result = await readJsonResponse<{
        success?: boolean;
        error?: string;
        data?: {
          jobs?: JobCost[];
          summary?: CostSummary;
          team_members?: TeamMemberCost[];
          team_summary?: TeamSummary;
          meta?: JobCostingApiMeta;
        };
      }>(response);

      if (!response.ok) {
        throw new Error(result?.error || `Failed to load job costing data (${response.status})`);
      }

      if (!result) {
        setJobs([]);
        setServerSummary(EMPTY_COST_SUMMARY);
        setTeamMembers([]);
        setServerTeamSummary(EMPTY_TEAM_SUMMARY);
        setServerMeta(null);
        return;
      }

      if (!result.success) {
        throw new Error(result.error || `Failed to load job costing data (${response.status})`);
      }

      const loadedJobs = Array.isArray(result.data?.jobs) ? result.data.jobs : [];
      setJobs(loadedJobs);
      // Accumulate manager options across fetches so the filter options don't
      // disappear while a specific manager is selected.
      setManagerOptions((prev) => {
        const merged = new Map(prev.map((m) => [m.id, m.name]));
        for (const job of loadedJobs) {
          if (job.manager_id != null && !merged.has(Number(job.manager_id))) {
            merged.set(Number(job.manager_id), job.manager_name || `User #${job.manager_id}`);
          }
        }
        return Array.from(merged.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name));
      });
      setServerSummary(result.data?.summary || EMPTY_COST_SUMMARY);
      setTeamMembers(Array.isArray(result.data?.team_members) ? result.data.team_members : []);
      setServerTeamSummary(result.data?.team_summary || EMPTY_TEAM_SUMMARY);
      setServerMeta(result.data?.meta || null);
    } catch (error) {
      logger.error('Error loading jobs:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to load job costing data');
    } finally {
      setLoading(false);
    }
  }, [includeDescendants, normalizedItemTypes, normalizedRootItemIds, templateKey, managerFilter]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const summary: CostSummary = useMemo(() => {
    // Use server-side summary (aggregated from work_item_bom_items, timers, billing, expenses) when available
    if (serverSummary) return serverSummary;

    const totalMaterial = jobs.reduce((sum, j) => sum + j.material_cost, 0);
    const totalLabor = jobs.reduce((sum, j) => sum + j.labor_cost, 0);
    const totalBomLabor = jobs.reduce((sum, j) => sum + (j.bom_labor_cost || 0), 0);
    const totalTimerLabor = jobs.reduce((sum, j) => sum + (j.timer_labor_cost || 0), 0);
    const totalBilledTime = jobs.reduce((sum, j) => sum + (j.billed_time_amount || 0), 0);
    const totalBilledLabourHours = jobs.reduce((sum, j) => sum + (j.billed_labour_hours || 0), 0);
    const totalOutsourcing = jobs.reduce((sum, j) => sum + j.outsourcing_cost, 0);
    const totalOverhead = jobs.reduce((sum, j) => sum + j.overhead_cost, 0);
    const totalExpenses = jobs.reduce((sum, j) => sum + (j.expense_cost || 0), 0);
    const totalCost = jobs.reduce((sum, j) => sum + j.total_cost, 0);
    const totalQuoted = jobs.reduce((sum, j) => sum + j.quoted_price, 0);
    const totalActualRevenue = jobs.reduce((sum, j) => sum + (j.actual_revenue || 0), 0);
    const totalPendingBilling = jobs.reduce((sum, j) => sum + (j.pending_billing_amount || 0), 0);
    const totalEffectiveRevenue = jobs.reduce((sum, j) => sum + (j.effective_revenue || j.quoted_price), 0);
    const totalProfit = jobs.reduce((sum, j) => sum + j.profit_margin, 0);
    const avgProfitMargin = totalEffectiveRevenue > 0
      ? (totalProfit / totalEffectiveRevenue) * 100
      : 0;
    const profitableJobs = jobs.filter(j => j.is_profitable).length;
    const unprofitableJobs = jobs.filter(j => !j.is_profitable).length;
    const totalLaborHours = jobs.reduce((sum, j) => sum + j.labor_hours, 0);
    const totalBomLaborHours = jobs.reduce((sum, j) => sum + (j.bom_labor_hours || 0), 0);
    const totalTimerLaborHours = jobs.reduce((sum, j) => sum + (j.timer_labor_hours || 0), 0);

    return {
      totalMaterial,
      totalLabor,
      totalBomLabor,
      totalTimerLabor,
      totalBilledTime,
      totalBilledLabourHours,
      totalOutsourcing,
      totalOverhead,
      totalExpenses,
      totalCost,
      totalQuoted,
      totalActualRevenue,
      totalPendingBilling,
      totalEffectiveRevenue,
      totalProfit,
      avgProfitMargin,
      profitableJobs,
      unprofitableJobs,
      totalJobs: jobs.length,
      jobsWithBom: jobs.filter(j => j.has_bom_data).length,
      jobsWithTimers: jobs.filter(j => j.has_timer_data).length,
      jobsWithBilling: jobs.filter(j => j.has_billing_data).length,
      totalLaborHours,
      totalBomLaborHours,
      totalTimerLaborHours,
    };
  }, [jobs, serverSummary]);

  // Report the summary-card row up to the shared StrictModuleHeader. When a
  // host provides onStatsChange, the first card row renders there (Row C)
  // instead of inline below.
  useEffect(() => {
    if (!onStatsChange) return;
    if (loading) {
      onStatsChange([]);
      return;
    }
    onStatsChange([
      {
        id: 'costing-total-costs',
        label: 'Total Costs',
        value: `R${summary.totalCost.toLocaleString()}`,
        icon: Calculator,
        subtitle: summaryCountLabel,
      },
      {
        id: 'costing-total-revenue',
        label: 'Total Revenue',
        value: `R${summary.totalEffectiveRevenue.toLocaleString()}`,
        icon: TrendingUp,
        subtitle: summary.totalActualRevenue > 0 ? `R${summary.totalActualRevenue.toLocaleString()} invoiced` : undefined,
      },
      {
        id: 'costing-total-profit',
        label: 'Total Profit',
        value: `R${summary.totalProfit.toLocaleString()}`,
        icon: DollarSign,
      },
      {
        id: 'costing-avg-margin',
        label: 'Avg Margin',
        value: `${summary.avgProfitMargin.toFixed(1)}%`,
        icon: Percent,
        subtitle: `${summary.profitableJobs}/${jobs.length} profitable`,
      },
    ]);
  }, [onStatsChange, loading, summary, summaryCountLabel, jobs.length]);

  const summaryCardsHoisted = onStatsChange != null;

  const filteredCostCategories = useMemo(() => getFilteredCostCategories(showOutsourcing, summary.totalExpenses > 0), [showOutsourcing, summary.totalExpenses]);

  const costBreakdownData = useMemo(() => [
    { name: 'Material/BOM', value: summary.totalMaterial, color: '#3b82f6' },
    { name: 'Labor (BOM + Timer)', value: summary.totalLabor, color: '#10b981' },
    ...(showOutsourcing ? [{ name: 'Outsourcing', value: summary.totalOutsourcing, color: '#f59e0b' }] : []),
    { name: 'Overhead', value: summary.totalOverhead, color: '#8b5cf6' },
    ...(summary.totalExpenses > 0 ? [{ name: 'Expenses', value: summary.totalExpenses, color: '#ec4899' }] : []),
  ].filter(d => d.value > 0), [summary, showOutsourcing]);

  const profitTrendData = useMemo(() => {
    // Group by month for trend.
    // Completed jobs use completed_at as the reference date (realized profit).
    // In-progress/open jobs use created_at and are tracked as WIP (not yet realized).
    const monthlyData: Record<string, { month: string; cost: number; revenue: number; profit: number; wipCost: number; wipRevenue: number }> = {};

    jobs.forEach(job => {
      const isCompleted = isTerminalWorkItemStatus(job.status, job.item_type);
      // Use completed_at for completed jobs, created_at for in-progress jobs
      const referenceDateStr = isCompleted ? (job.completed_at || job.created_at) : job.created_at;
      const date = new Date(referenceDateStr);
      if (Number.isNaN(date.getTime())) return;

      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthLabel, cost: 0, revenue: 0, profit: 0, wipCost: 0, wipRevenue: 0 };
      }

      // Use effective_revenue (actual invoiced if available, otherwise quoted) for accurate trend
      const jobRevenue = job.effective_revenue || job.quoted_price;

      if (isCompleted) {
        // Realized revenue/cost/profit for completed jobs
        monthlyData[monthKey].cost += job.total_cost;
        monthlyData[monthKey].revenue += jobRevenue;
        monthlyData[monthKey].profit += job.profit_margin;
      } else {
        // WIP (work in progress) — not yet realized
        monthlyData[monthKey].wipCost += job.total_cost;
        monthlyData[monthKey].wipRevenue += jobRevenue;
      }
    });

    // Sort by month key and take last 6
    return Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([, value]) => ({
        month: value.month,
        cost: Math.round(value.cost * 100) / 100,
        revenue: Math.round(value.revenue * 100) / 100,
        profit: Math.round(value.profit * 100) / 100,
      }));
  }, [jobs]);

  const resolvedTeamSummary = useMemo(() => {
    if (serverTeamSummary.total_members > 0) {
      return serverTeamSummary;
    }

    if (teamMembers.length === 0) {
      return EMPTY_TEAM_SUMMARY;
    }

    const aggregate = teamMembers.reduce((acc, member) => {
      acc.total_members += 1;
      acc.total_hours += member.metrics.total_hours;
      acc.total_billable_hours += member.metrics.billable_hours;
      acc.total_travel_hours += member.metrics.travel_hours;
      acc.total_time_cost += member.metrics.time_cost ?? 0;
      acc.total_appointments_missed += member.metrics.appointments_missed || 0;
      acc.avg_billable_ratio += member.scores.billable_ratio;
      return acc;
    }, { ...EMPTY_TEAM_SUMMARY });

    return {
      ...aggregate,
      total_hours: Math.round(aggregate.total_hours * 100) / 100,
      total_billable_hours: Math.round(aggregate.total_billable_hours * 100) / 100,
      total_travel_hours: Math.round(aggregate.total_travel_hours * 100) / 100,
      total_time_cost: Math.round(aggregate.total_time_cost * 100) / 100,
      avg_billable_ratio: Math.round((aggregate.avg_billable_ratio / teamMembers.length) * 100) / 100,
    };
  }, [serverTeamSummary, teamMembers]);

  const topTeamContributor = useMemo(() => {
    if (teamMembers.length === 0) return null;
    return [...teamMembers].sort((a, b) => (b.metrics.time_cost ?? 0) - (a.metrics.time_cost ?? 0) || b.metrics.total_hours - a.metrics.total_hours)[0] || null;
  }, [teamMembers]);

  const maxTeamHours = useMemo(() => {
    return Math.max(1, ...teamMembers.map((member) => member.metrics.total_hours));
  }, [teamMembers]);

  const jobsById = useMemo(() => {
    return new Map(jobs.map((job) => [job.id, job]));
  }, [jobs]);

  const filteredTeamMembers = useMemo(() => {
    const normalizedQuery = teamSearchQuery.trim().toLowerCase();
    const filtered = teamMembers.filter((member) => {
      if (!normalizedQuery) return true;

      if (member.user_name.toLowerCase().includes(normalizedQuery)) return true;

      return member.item_breakdown.some((breakdown) => {
        const item = jobsById.get(breakdown.work_item_id);
        return Boolean(
          item?.job_number?.toLowerCase().includes(normalizedQuery)
          || item?.job_title?.toLowerCase().includes(normalizedQuery)
          || item?.customer_name?.toLowerCase().includes(normalizedQuery)
        );
      });
    });

    return [...filtered].sort((a, b) => {
      switch (teamSortBy) {
        case 'name_asc':
          return a.user_name.localeCompare(b.user_name);
        case 'hours_desc':
          return b.metrics.total_hours - a.metrics.total_hours || (b.metrics.time_cost ?? 0) - (a.metrics.time_cost ?? 0);
        case 'billable_desc':
          return b.scores.billable_ratio - a.scores.billable_ratio || (b.metrics.time_cost ?? 0) - (a.metrics.time_cost ?? 0);
        case 'efficiency_desc':
          return b.scores.travel_efficiency - a.scores.travel_efficiency || (b.metrics.time_cost ?? 0) - (a.metrics.time_cost ?? 0);
        case 'cost_desc':
        default:
          return (b.metrics.time_cost ?? 0) - (a.metrics.time_cost ?? 0) || b.metrics.total_hours - a.metrics.total_hours;
      }
    });
  }, [jobsById, teamMembers, teamSearchQuery, teamSortBy]);

  // ── COGS Recognition (Phase 12, Issue S) ──────────────────────────────
  const handleRecognizeCOGS = useCallback(async (jobId: string, jobTitle: string) => {
    setRecognizingCOGS(jobId);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const response = await fetch(`/api/v1/job-costing/${jobId}/recognize-cogs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const data = await readJsonResponse<{ success: boolean; data?: { already_recognized: boolean; journal_entry_id: number; entry_number: string; total_amount: number }; error?: string }>(response);
      if (response.ok && data?.success && data.data) {
        const result = data.data;
        if (result.already_recognized && result.journal_entry_id === 0) {
          notify.info(`All costs for "${jobTitle}" already recognized via goods issue`);
        } else if (result.already_recognized) {
          notify.info(`COGS already recognized for "${jobTitle}" (JE: ${result.entry_number})`);
        } else {
          notify.success(`COGS recognized for "${jobTitle}": ${result.total_amount} via ${result.entry_number}`);
        }
      } else {
        notify.error(data?.error || 'Failed to recognize COGS');
      }
    } catch {
      notify.error('Failed to recognize COGS');
    } finally {
      setRecognizingCOGS(null);
    }
  }, []);

  const handleOpenItem = useCallback(async (item: JobCost) => {
    if (!item?.id || openingItemId === item.id) {
      return;
    }

    setOpeningItemId(item.id);

    try {
      if (onOpenItem) {
        await onOpenItem(item);

        emitOperationsGuidedTourTrainingEvent({
          type: 'job_costing_item_opened',
          route: 'job_costing_dashboard',
          workItemId: item.id,
          metadata: {
            templateKey: templateKey || null,
            source: 'job_costing_dashboard',
          },
        });

        return;
      }

      let token = item.share_token || null;

      if (!token) {
        const accessToken = authService.getTokens()?.access_token;
        if (!accessToken) {
          throw new Error('Authentication required to open this item');
        }

        const response = await fetch(`/api/v1/work-items/${item.id}/permalink`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await readJsonResponse<{ success?: boolean; token?: string; error?: string }>(response);

        if (!response.ok || !result?.success || !result.token) {
          throw new Error(result?.error || `Failed to open item (${response.status})`);
        }

        token = result.token;
      }

      emitOperationsGuidedTourTrainingEvent({
        type: 'job_costing_item_opened',
        route: 'job_costing_dashboard',
        workItemId: item.id,
        metadata: {
          templateKey: templateKey || null,
          source: 'job_costing_dashboard',
        },
      });

      router.push(`/wi/${token}?returnTo=${encodeURIComponent(getCurrentReturnToPath())}`);
    } catch (error) {
      logger.error('Failed to open costing item:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to open item');
    } finally {
      setOpeningItemId((current) => current === item.id ? null : current);
    }
  }, [onOpenItem, openingItemId, router, templateKey]);

  const handleOpenTeamMember = useCallback((userId: number) => {
    if (!Number.isInteger(userId) || userId <= 0) {
      notify.error('Unable to open team member profile');
      return;
    }

    openTeamMember(userId);
  }, [openTeamMember]);

  if (loading) {
    return (
      <div data-tour-id="job-costing-dashboard-root" className={`flex items-center justify-center ${embedded ? 'min-h-[320px]' : 'h-96'}`}>
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Loading {collectionEntityLabelLower} billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-tour-id="job-costing-dashboard-root" className={`${embedded ? '' : 'h-full'} overflow-auto ${embedded ? '' : isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${embedded ? 'px-0 pb-5' : 'p-6'} ${embedded ? '' : 'border-b border-gray-200 dark:border-gray-700'}`}>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <DollarSign className="w-7 h-7" style={{ color: primaryColor }} />
              {resolvedTitle}
              <UserDocsLink docsPath="/total-operations/workflows/service-operations/job-costing" label="User guide: Job costing & COGS" />
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {resolvedSubtitle}
            </p>
          </div>
          {showHeaderControls && (
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:w-auto xl:justify-end">
              {managerOptions.length > 0 && (
                <select
                  value={managerFilter === 'all' ? 'all' : String(managerFilter)}
                  onChange={(e) => setManagerFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className={`w-full rounded-lg border px-3 py-2 text-sm sm:w-auto ${isDark ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                  title="Filter by responsible manager"
                >
                  <option value="all">All managers</option>
                  {managerOptions.map((manager) => (
                    <option key={manager.id} value={manager.id}>{manager.name}</option>
                  ))}
                </select>
              )}
              <div className="flex items-center gap-3 sm:w-auto">
                <button
                  onClick={() => {
                    const completedJobs = jobs.filter(j => isTerminalWorkItemStatus(j.status));
                    if (completedJobs.length === 0) {
                      notify.info('No completed jobs to recognize COGS for');
                      return;
                    }
                    completedJobs.forEach(j => void handleRecognizeCOGS(j.id, j.job_title));
                  }}
                  className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium sm:flex-none ${isDark ? 'border-emerald-700 text-emerald-400 hover:bg-emerald-900/20' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                  title="Recognize deferred COGS for all completed jobs"
                >
                  <Calculator className="w-4 h-4 inline mr-1" />
                  Recognize COGS
                </button>
                <button onClick={() => loadJobs()} className={`flex-1 rounded-lg border p-2 sm:flex-none ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                  <RefreshCw className="w-5 h-5 mx-auto" />
                </button>
                <button className={`flex-1 rounded-lg border p-2 sm:flex-none ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                  <Download className="w-5 h-5 mx-auto" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards — hoisted into the shared StrictModuleHeader when a
          host provides onStatsChange */}
      <div className={`${embedded ? 'px-0 pb-0' : 'p-6'} space-y-6`}>
        {!summaryCardsHoisted && (
        <div data-tour-id="job-costing-summary-cards" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CostCard
            title="Total Costs"
            value={summary.totalCost}
            icon={Calculator}
            color="#ef4444"
            subtitle={summaryCountLabel}
          />
          <CostCard
            title="Total Revenue"
            value={summary.totalEffectiveRevenue}
            icon={TrendingUp}
            color="#10b981"
            subtitle={summary.totalActualRevenue > 0 ? `R${summary.totalActualRevenue.toLocaleString()} invoiced` : undefined}
          />
          <CostCard
            title="Total Profit"
            value={summary.totalProfit}
            icon={DollarSign}
            color={summary.totalProfit >= 0 ? '#10b981' : '#ef4444'}
            trend={5.2}
          />
          <CostCard
            title="Avg Margin"
            value={summary.avgProfitMargin}
            icon={Percent}
            color="#6366f1"
            subtitle={`${summary.profitableJobs}/${jobs.length} profitable`}
          />
        </div>
        )}

        {/* Data Source Indicators */}
        {(summary.jobsWithTimers > 0 || summary.jobsWithBilling > 0 || summary.totalExpenses > 0) && (
          <div className={`flex flex-wrap items-center gap-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className="font-medium">Data sources:</span>
            {summary.jobsWithBom > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <Package className="w-3 h-3" /> BOM ({summary.jobsWithBom})
              </span>
            )}
            {summary.jobsWithTimers > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <Clock className="w-3 h-3" /> Timers ({summary.jobsWithTimers})
              </span>
            )}
            {summary.jobsWithBilling > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <Receipt className="w-3 h-3" /> Billing ({summary.jobsWithBilling})
              </span>
            )}
            {summary.totalExpenses > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <Receipt className="w-3 h-3" /> Expenses (R{summary.totalExpenses.toLocaleString()})
              </span>
            )}
            {summary.totalActualRevenue > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700'}`}>
                <TrendingUp className="w-3 h-3" /> Billed: R{summary.totalActualRevenue.toLocaleString()}
              </span>
            )}
            {(summary.totalPendingBilling ?? 0) > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                <Receipt className="w-3 h-3" /> Pending billing: R{(summary.totalPendingBilling ?? 0).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* Cost Breakdown Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {filteredCostCategories.map(cat => {
            const value = cat.key === 'material' ? summary.totalMaterial :
                          cat.key === 'labor' ? summary.totalLabor :
                          cat.key === 'outsourcing' ? summary.totalOutsourcing :
                          cat.key === 'expense' ? summary.totalExpenses :
                          summary.totalOverhead;
            const percentage = summary.totalCost > 0 ? (value / summary.totalCost) * 100 : 0;
            
            return (
              <div key={cat.key} className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{cat.label}</span>
                </div>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  R{value.toLocaleString()}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: cat.color }} />
                  </div>
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{percentage.toFixed(1)}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cost Breakdown Pie */}
          <div data-tour-id="job-costing-cost-distribution" className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <PieChart className="w-5 h-5" style={{ color: primaryColor }} />
              Cost Distribution
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <RechartsPie>
                <Pie
                  data={costBreakdownData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  label={({ name, percent }) => `${name} ${((typeof percent === 'number' ? percent : 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {costBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => formatCurrency(normalizeTooltipCurrencyValue(value))}
                  contentStyle={{ 
                    backgroundColor: isDark ? '#1f2937' : '#fff',
                    border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {costBreakdownData.map(item => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Profit Trend */}
          <div data-tour-id="job-costing-profit-trend" className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <BarChart3 className="w-5 h-5" style={{ color: primaryColor }} />
              Revenue vs Cost Trend
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={profitTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="month" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={12} />
                <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={12} tickFormatter={(v) => `R${v/1000}k`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(normalizeTooltipCurrencyValue(value))}
                  contentStyle={{ 
                    backgroundColor: isDark ? '#1f2937' : '#fff',
                    border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="cost" name="Cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="profit" name="Profit" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Team Members */}
        <div data-tour-id="job-costing-team-performance" className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div
            className={`flex items-center justify-between px-4 py-3 cursor-pointer ${isDark ? 'hover:bg-gray-800/80' : 'hover:bg-gray-50'}`}
            onClick={() => setTeamCollapsed((collapsed) => !collapsed)}
          >
            <div>
              <h3 className={`font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {teamCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <Users className="w-5 h-5" style={{ color: primaryColor }} />
                Team Costing & Performance
              </h3>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {serverMeta?.team_data_source === 'job_costing_api'
                  ? `Server-calculated from tracked time, linked assignments, and ${serverMeta.include_descendants ? 'descendant' : 'direct'} ${collectionEntityLabelLower}`
                  : `Based on tracked time plus linked task and appointment assignments across loaded ${collectionEntityLabelLower}`}
              </p>
            </div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {resolvedTeamSummary.total_members} member{resolvedTeamSummary.total_members === 1 ? '' : 's'}
            </div>
          </div>

          {!teamCollapsed && (
            <div className="p-4 pt-0 space-y-4">
              {teamMembers.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <TeamMetricCard
                      icon={Users}
                      label="Team Members"
                      value={resolvedTeamSummary.total_members}
                      subValue={`${teamMembers.length === 1 ? '1 active contributor' : `${teamMembers.length} active contributors`}`}
                      color="#3b82f6"
                      isDark={isDark}
                    />
                    <TeamMetricCard
                      icon={Clock}
                      label="Tracked Hours"
                      value={formatHours(resolvedTeamSummary.total_hours)}
                      subValue={`${formatHours(resolvedTeamSummary.total_billable_hours)} billable`}
                      color="#8b5cf6"
                      isDark={isDark}
                    />
                    <TeamMetricCard
                      icon={DollarSign}
                      label="Time Cost"
                      value={formatCurrency(resolvedTeamSummary.total_time_cost)}
                      subValue={`${formatHours(resolvedTeamSummary.total_travel_hours)} travel time • ${formatCurrency(filteredTeamMembers.reduce((sum, member) => sum + (member.profitability?.estimated_recovery ?? 0), 0))} recovery est.`}
                      color="#10b981"
                      isDark={isDark}
                    />
                    <TeamMetricCard
                      icon={Target}
                      label="Avg Billable Ratio"
                      value={`${Math.round(resolvedTeamSummary.avg_billable_ratio)}%`}
                      subValue={topTeamContributor ? `${topTeamContributor.user_name} leads by cost` : 'No contributor leader yet'}
                      color="#f59e0b"
                      isDark={isDark}
                    />
                  </div>

                  <div className="flex flex-col gap-3 xl:flex-row">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={teamSearchQuery}
                        onChange={(e) => setTeamSearchQuery(e.target.value)}
                        placeholder="Search team members or related items..."
                        className={`w-full pl-9 pr-4 py-2 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                    <select
                      value={teamSortBy}
                      onChange={(e) => setTeamSortBy(e.target.value as TeamSortOption)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="cost_desc">Sort: Highest cost</option>
                      <option value="hours_desc">Sort: Most hours</option>
                      <option value="billable_desc">Sort: Billable ratio</option>
                      <option value="efficiency_desc">Sort: Travel efficiency</option>
                      <option value="name_asc">Sort: Name</option>
                    </select>
                  </div>

                  <div className="space-y-3 lg:hidden">
                    <div className={`rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-gray-700 bg-gray-900/60 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      Cost rate comes from the team member&apos;s payroll contract salary (wf_contracts) where configured, else their employee `hourly_rate`. Target recovery rate is derived from their earning target and billable-hours target, while recovery estimates apply the active client labour and travel billing rates to tracked work.
                    </div>
                    {filteredTeamMembers.map((member) => {
                      const isExpanded = expandedMember === member.user_id;
                      return (
                        <div key={member.user_id} className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-white'}`}>
                          <button
                            type="button"
                            className="w-full p-4 text-left"
                            onClick={() => setExpandedMember(isExpanded ? null : member.user_id)}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-start gap-3">
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={`mt-0.5 flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-xs font-semibold ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenTeamMember(member.user_id);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleOpenTeamMember(member.user_id);
                                    }
                                  }}
                                  title={`Open ${member.user_name}`}
                                >
                                  {member.user_name.split(' ').map((word) => word[0]).join('').substring(0, 2).toUpperCase() || 'TM'}
                                </div>
                                <div className="min-w-0">
                                  <p className={`truncate font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{member.user_name}</p>
                                  <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {member.metrics.items_worked} {member.metrics.items_worked === 1 ? genericEntityLabel.toLowerCase() : collectionEntityLabelLower}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <ScoreBadge score={member.scores.completion_rate || member.scores.billable_ratio} isDark={isDark} />
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                              <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50'}`}>
                                <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Hours</p>
                                <p className={`mt-1 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatHours(member.metrics.total_hours)}</p>
                              </div>
                              <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50'}`}>
                                <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Billable</p>
                                <p className={`mt-1 font-semibold ${member.scores.billable_ratio >= 80 ? 'text-green-500' : member.scores.billable_ratio >= 60 ? (isDark ? 'text-yellow-400' : 'text-yellow-600') : 'text-red-500'}`}>
                                  {Math.round(member.scores.billable_ratio)}%
                                </p>
                              </div>
                              <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50'}`}>
                                <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Time Cost</p>
                                <p className={`mt-1 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{member.metrics.time_cost != null ? formatCurrency(member.metrics.time_cost) : '—'}</p>
                              </div>
                              <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50'}`}>
                                <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Margin</p>
                                <p className={`mt-1 font-semibold ${(member.profitability?.recovery_margin ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{member.profitability ? formatCurrency(member.profitability.recovery_margin) : '—'}</p>
                              </div>
                            </div>
                          </button>

                          {isExpanded && (
                            <TeamMemberExpandedContent
                              member={member}
                              isDark={isDark}
                              maxTeamHours={maxTeamHours}
                              jobsById={jobsById}
                              handleOpenItem={handleOpenItem}
                              openingItemId={openingItemId}
                              collectionEntityLabel={collectionEntityLabel}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="hidden overflow-x-auto lg:block">
                    <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-gray-700 bg-gray-900/60 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      Cost rate comes from the team member&apos;s payroll contract salary (wf_contracts) where configured, else their employee `hourly_rate`. Target recovery rate is derived from their earning target and billable-hours target, while recovery estimates apply the active client labour and travel billing rates to tracked work.
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDark ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                          <th className="text-left px-4 py-3 font-medium">Person</th>
                          <th className="text-right px-3 py-3 font-medium">Hours</th>
                          <th className="text-right px-3 py-3 font-medium hidden sm:table-cell">Billable</th>
                          <th className="text-right px-3 py-3 font-medium hidden md:table-cell">Travel</th>
                          <th className="text-right px-3 py-3 font-medium">Items</th>
                          <th className="text-right px-3 py-3 font-medium hidden lg:table-cell">Appts</th>
                          <th className="text-right px-3 py-3 font-medium hidden xl:table-cell">Cost / hr</th>
                          <th className="text-right px-3 py-3 font-medium hidden xl:table-cell">Target / hr</th>
                          <th className="text-right px-3 py-3 font-medium hidden xl:table-cell">Recovery</th>
                          <th className="text-right px-3 py-3 font-medium">Time Cost</th>
                          <th className="text-right px-3 py-3 font-medium">Margin</th>
                          <th className="text-right px-3 py-3 font-medium">Score</th>
                          <th className="w-5" />
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {filteredTeamMembers.map((member) => {
                          const isExpanded = expandedMember === member.user_id;
                          return (
                            <React.Fragment key={member.user_id}>
                              <tr
                                className={`${isDark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                                onClick={() => setExpandedMember(isExpanded ? null : member.user_id)}
                              >
                                <td className="px-4 py-3">
                                  <button
                                    type="button"
                                    className="flex items-center gap-2 min-w-0 text-left group"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenTeamMember(member.user_id);
                                    }}
                                    onKeyDown={(event) => {
                                      event.stopPropagation();
                                    }}
                                    title={`Open ${member.user_name}`}
                                  >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${isDark ? 'bg-blue-900/40 text-blue-400 group-hover:bg-blue-900/60' : 'bg-blue-100 text-blue-700 group-hover:bg-blue-200'}`}>
                                      {member.user_name.split(' ').map((word) => word[0]).join('').substring(0, 2).toUpperCase() || 'TM'}
                                    </div>
                                    <div className="min-w-0">
                                      <p className={`font-medium truncate transition-colors ${isDark ? 'text-white group-hover:text-blue-300' : 'text-gray-900 group-hover:text-blue-700'}`}>{member.user_name}</p>
                                      <p className={`text-xs ${isDark ? 'text-gray-500 group-hover:text-gray-400' : 'text-gray-400 group-hover:text-gray-500'}`}>
                                        {member.metrics.items_worked} {member.metrics.items_worked === 1 ? genericEntityLabel.toLowerCase() : collectionEntityLabelLower}
                                        {' '}• Open profile
                                      </p>
                                    </div>
                                  </button>
                                </td>
                                <td className="text-right px-3 py-3 tabular-nums">{formatHours(member.metrics.total_hours)}</td>
                                <td className="text-right px-3 py-3 tabular-nums hidden sm:table-cell">
                                  <span className={member.scores.billable_ratio >= 80 ? 'text-green-500' : member.scores.billable_ratio >= 60 ? (isDark ? 'text-yellow-400' : 'text-yellow-600') : 'text-red-500'}>
                                    {Math.round(member.scores.billable_ratio)}%
                                  </span>
                                </td>
                                <td className={`text-right px-3 py-3 tabular-nums hidden md:table-cell ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {formatHours(member.metrics.travel_hours)}
                                </td>
                                <td className="text-right px-3 py-3 tabular-nums">{member.metrics.items_worked}</td>
                                <td className="text-right px-3 py-3 tabular-nums hidden lg:table-cell">
                                  {member.metrics.appointments_completed}/{member.metrics.appointments_assigned}
                                  {(member.metrics.appointments_missed || 0) > 0 && (
                                    <span className="text-red-500"> · {member.metrics.appointments_missed} missed</span>
                                  )}
                                </td>
                                <td className="text-right px-3 py-3 tabular-nums hidden xl:table-cell">{member.rates ? formatRate(member.rates.cost_rate) : '—'}</td>
                                <td className="text-right px-3 py-3 tabular-nums hidden xl:table-cell">{member.rates ? formatRate(member.rates.target_recovery_rate) : '—'}</td>
                                <td className="text-right px-3 py-3 tabular-nums hidden xl:table-cell font-medium">{member.profitability ? formatCurrency(member.profitability.estimated_recovery) : '—'}</td>
                                <td className="text-right px-3 py-3 tabular-nums font-medium">{member.metrics.time_cost != null ? formatCurrency(member.metrics.time_cost) : '—'}</td>
                                <td className={`text-right px-3 py-3 tabular-nums font-medium ${(member.profitability?.recovery_margin ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{member.profitability ? formatCurrency(member.profitability.recovery_margin) : '—'}</td>
                                <td className="text-right px-3 py-3"><ScoreBadge score={member.scores.completion_rate || member.scores.billable_ratio} isDark={isDark} /></td>
                                <td className="px-2 py-3">{isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td colSpan={12}>
                                    <TeamMemberExpandedContent
                                      member={member}
                                      isDark={isDark}
                                      maxTeamHours={maxTeamHours}
                                      jobsById={jobsById}
                                      handleOpenItem={handleOpenItem}
                                      openingItemId={openingItemId}
                                      collectionEntityLabel={collectionEntityLabel}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {filteredTeamMembers.length === 0 && (
                    <div className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
                      No team members match the current search.
                    </div>
                  )}
                </>
              ) : (
                <div className="py-10 text-center">
                  <Users className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    No team member data yet
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Team costing will appear once time entries or linked assignments are recorded.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
